import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { FiSave, FiArrowLeft, FiUpload, FiX, FiImage } from 'react-icons/fi';
import axios from '../config/axios';
import { useTranslation } from '../hooks/useTranslation';

const ArticleEditor = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [article, setArticle] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'nature',
    challengeId: null,
    status: 'draft',
    metaTitle: '',
    metaDescription: ''
  });

  const [headerImage, setHeaderImage] = useState(null);
  const [articleImages, setArticleImages] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  const categories = ['nature', 'urban', 'historic', 'adventure', 'water', 'park'];

  useEffect(() => {
    loadChallenges();
    if (isEdit) {
      loadArticle();
    }
  }, [id]);

  const loadArticle = async () => {
    try {
      const response = await axios.get(`/api/cms/articles/${id}`);
      setArticle(response.data);
      setArticleImages(response.data.images || []);
    } catch (error) {
      console.error('Error loading article:', error);
      alert('Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  const loadChallenges = async () => {
    try {
      const response = await axios.get('/api/challenges');
      setChallenges(response.data);
    } catch (error) {
      console.error('Error loading challenges:', error);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleTitleChange = (title) => {
    setArticle(prev => ({
      ...prev,
      title,
      slug: generateSlug(title)
    }));
  };

  const handleSave = async (publishNow = false) => {
    if (!article.title || !article.content || !article.category) {
      alert('Please fill in title, content, and category');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...article,
        status: publishNow ? 'published' : article.status
      };

      let savedArticle;
      if (isEdit) {
        const response = await axios.put(`/api/cms/articles/${id}`, payload);
        savedArticle = response.data.article;
      } else {
        const response = await axios.post('/api/cms/articles', payload);
        savedArticle = response.data.article;
      }

      // Upload header image if selected
      if (headerImage) {
        const formData = new FormData();
        formData.append('header', headerImage);
        await axios.post(`/api/cms/articles/${savedArticle.id}/header-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      alert(publishNow ? 'Article published successfully!' : 'Article saved successfully!');
      navigate('/articles');
    } catch (error) {
      console.error('Error saving article:', error);
      alert('Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handleHeaderImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setHeaderImage(file);
    }
  };

  const handleArticleImagesUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !isEdit) return;

    const formData = new FormData();
    files.forEach(file => {
      formData.append('images', file);
    });

    try {
      const response = await axios.post(`/api/cms/articles/${id}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setArticleImages(prev => [...prev, ...response.data.images]);
      alert('Images uploaded successfully!');
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Failed to upload images');
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;

    try {
      await axios.delete(`/api/cms/article-images/${imageId}`);
      setArticleImages(prev => prev.filter(img => img.id !== imageId));
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete image');
    }
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">{t('cms.common.loading')}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/articles')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-primary-600">
              {isEdit ? t('cms.articles.editArticle') : t('cms.articles.addArticle')}
            </h1>
            <p className="text-gray-600 mt-1">Create engaging content for your website</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <FiSave className="w-4 h-4" />
            {t('cms.common.save')}
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={saving}
            className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Publish'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
            <input
              type="text"
              value={article.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Enter article title..."
              className="w-full px-4 py-3 text-xl font-semibold border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <div className="mt-2 text-sm text-gray-500">
              Slug: <span className="font-mono">{article.slug || 'auto-generated'}</span>
            </div>
          </div>

          {/* Header Image */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Header Image</label>
            {(headerImage || article.header_image_url) && (
              <div className="mb-4">
                <img
                  src={headerImage ? URL.createObjectURL(headerImage) : `${process.env.REACT_APP_API_URL.replace('/api', '')}${article.header_image_url}`}
                  alt="Header"
                  className="w-full h-64 object-cover rounded-lg"
                />
              </div>
            )}
            <label className="cursor-pointer">
              <div className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 transition-colors">
                <FiUpload className="w-5 h-5 text-gray-400" />
                <span className="text-gray-600">Upload Header Image</span>
              </div>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleHeaderImageUpload}
              />
            </label>
          </div>

          {/* Excerpt */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Excerpt</label>
            <textarea
              value={article.excerpt}
              onChange={(e) => setArticle(prev => ({ ...prev, excerpt: e.target.value }))}
              placeholder="Short description for article listings..."
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Content Editor */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Content *</label>
            <ReactQuill
              theme="snow"
              value={article.content}
              onChange={(content) => setArticle(prev => ({ ...prev, content }))}
              modules={quillModules}
              className="h-96 mb-12"
            />
          </div>

          {/* Article Images Gallery */}
          {isEdit && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <label className="block text-sm font-medium text-gray-700">Article Images</label>
                <label className="cursor-pointer">
                  <div className="btn btn-primary flex items-center gap-2">
                    <FiImage className="w-4 h-4" />
                    <span>Add Images</span>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleArticleImagesUpload}
                  />
                </label>
              </div>
              {articleImages.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {articleImages.map(image => (
                    <div key={image.id} className="relative group">
                      <img
                        src={`${process.env.REACT_APP_API_URL.replace('/api', '')}${image.image_url}`}
                        alt={image.alt_text || 'Article image'}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <button
                        onClick={() => handleDeleteImage(image.id)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No images uploaded yet</p>
              )}
            </div>
          )}

          {/* SEO */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">SEO Settings</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Meta Title</label>
                <input
                  type="text"
                  value={article.meta_title}
                  onChange={(e) => setArticle(prev => ({ ...prev, meta_title: e.target.value }))}
                  placeholder="Leave empty to use article title"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Meta Description</label>
                <textarea
                  value={article.meta_description}
                  onChange={(e) => setArticle(prev => ({ ...prev, meta_description: e.target.value }))}
                  placeholder="SEO description (155 characters recommended)"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">{article.meta_description.length}/155 characters</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Category */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">Category *</label>
            <div className="space-y-2">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setArticle(prev => ({ ...prev, category }))}
                  className={`w-full px-4 py-2 rounded-lg border-2 transition-colors capitalize ${
                    article.category === category
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Challenge Link */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">Link to Challenge</label>
            <select
              value={article.challenge_id || ''}
              onChange={(e) => setArticle(prev => ({ ...prev, challenge_id: e.target.value ? parseInt(e.target.value) : null }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">No challenge</option>
              {challenges.map(challenge => (
                <option key={challenge.id} value={challenge.id}>
                  {challenge.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-2">
              Link this article to a specific challenge to show challenge details on the article page
            </p>
          </div>

          {/* Status */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">Status</label>
            <select
              value={article.status}
              onChange={(e) => setArticle(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleEditor;
